<template>
  <div v-loading="loading">
    <el-row type="flex" class="flex-wrap" :gutter="40">
      <el-col :md="6">
        <div class="app-summary">
          <div class="app-summary__title">
            <div class="image">
              <ra-avatar
                :src="app.customization && app.customization.company_logo"
                :size="40"
                :icon-type="'app'"
              />
            </div>
            <div class="title">
              <h6>{{ app.name || "-" }}</h6>
              <p>{{ app.description || "-" }}</p>
            </div>
          </div>
          <div class="app-summary__body">
            <div class="property app-code">
              <span>Application code</span>
              <p>
                <span class="long">{{ app.code || "-" }}</span>
                <i
                  v-if="userCan('authx', 'app.updatedata')"
                  class="ra-icon--copy"
                  v-clipboard:copy="app.code"
                  v-clipboard:success="copy"
                ></i>
              </p>
            </div>
            <div class="property">
              <span>Created</span>
              <p>{{ mFormatShortDate(app.created_at, "d-m-y") }}</p>
            </div>
            <div class="property">
              <span>Last updated</span>
              <p>{{ mFormatShortDate(app.modified_at, "d-m-y") }}</p>
            </div>
          </div>
          <div class="app-summary__footer">
            <el-button
              v-if="userCan('authx', 'global.getfieldtypes')"
              type="text"
              plain
              size="small"
              @click="configure(true)"
            >Preview</el-button
            >
            <el-button
              v-if="userCan('authx', 'app.updatedata')"
              type="primary"
              size="small"
              @click="configure(false)"
              >Configure</el-button
            >
          </div>
        </div>
      </el-col>
      <el-col :md="18">
        <div class="app-overview" v-loading="fetching">
          <div class="app-overview__action">
            <p>Showing data for</p>
            <ra-periods @set-period="setPeriod" />
          </div>
          <div class="app-overview__content">
            <el-row type="flex" class="flex-wrap" :gutter="20">
              <el-col :xs="24" :sm="8" :md="8" :lg="8">
                <ra-summary-card
                  label="Total Signups"
                  :value="stats.total_signups"
                />
              </el-col>
              <el-col :xs="24" :sm="8" :md="8" :lg="8">
                <ra-summary-card
                  label="Total Logins"
                  :value="stats.total_logins"
                />
              </el-col>
              <el-col :xs="24" :sm="8" :md="8" :lg="8">
                <ra-summary-card
                  label="Monthly Active Users"
                  :value="stats.active_users"
                />
              </el-col>
            </el-row>
            <el-row type="flex" class="flex-wrap" :gutter="20">
              <el-col :xs="24" :sm="12" :md="12" :lg="12">
                <signups-by-type :app-code="app.code" :period="period" />
              </el-col>
              <el-col :xs="24" :sm="12" :md="12" :lg="12">
                <logins-by-type :app-code="app.code" :period="period" />
              </el-col>
            </el-row>
            <el-row type="flex" class="flex-wrap" :gutter="20">
              <el-col :span="24">
                <daily-active-users :app-code="app.code" :period="period" />
              </el-col>
            </el-row>
          </div>
        </div>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import apiConstants from "@/apiConstants";
import * as mutations from "@/store/mutation-types";
import SignupsByType from "@/components/Products/AuthX/Applications/Overview/SignupsByType";
import LoginsByType from "@/components/Products/AuthX/Applications/Overview/LoginsByType";
import DailyActiveUsers from "@/components/Products/AuthX/Applications/Overview/DailyActiveUsers";

export default {
  name: "AuthX",
  components: {
    SignupsByType,
    LoginsByType,
    DailyActiveUsers
  },
  data() {
    return {
      loading: false,
      fetching: false,
      period: {
        start_at: "",
        end_at: ""
      },
      stats: {
        total_signups: 0,
        total_logins: 0,
        active_users: 0
      }
    };
  },
  computed: {
    app() {
      return this.$store.getters.authx_app;
    },
    appId() {
      return this.$route.params.id;
    }
  },
  methods: {
    setPeriod(period) {
      const { start_at, end_at } = period;
      this.period.start_at = start_at;
      this.period.end_at = end_at;

      if (this.app.code) {
        this.fetching = true;
        this.getAppSignups();
        this.getAppLogins();
        this.getAppActiveUsers();
      } else {
        this.getApp();
      }
    },
    copy() {
      this.notification(
        "AppCode",
        "Application code copied to clipboard.",
        "success"
      );
    },
    configure(preview) {
      if (preview) {
        this.$router.push({
          name: "authx.apps.configuration",
          params: {
            preview: "true"
          }
        });
      } else {
        this.$router.push({
          name: "authx.apps.configuration"
        });
      }
    },
    getApp() {
      this.loading = true;
      const apiUrl = `apps/${this.appId}`;
      this.$http
        .post(
          apiUrl,
          {
            method: "GET"
          },
          {
            headers: {
              "data-baseurl-namespace": apiConstants.URLS.AUTHX
            }
          }
        )
        .then(response => {
          const resData = response.data;
          if (!resData) {
            this.loading = false;
            return;
          }
          if (resData.status === "error") {
            this.handleErrors(resData);
            this.loading = false;
          } else {
            this.setAuthxApp(resData);
            this.loading = false;
            this.getAppSignups();
            this.getAppLogins();
            this.getAppActiveUsers();
          }
        })
        .catch(() => {
          this.notification("Error", "An unknown error occurred", "danger");
          this.loading = false;
        });
    },
    getAppSignups() {
      const { start_at, end_at } = this.period;
      const filter_by = "done_at";
      const queries = `start_at=${start_at}&end_at=${end_at}&filter_by=${filter_by}`;

      const apiUrl = `apps/${this.app.code}/stats/agg/sign-ups?${queries}`;
      this.$http
        .post(
          apiUrl,
          {
            method: "GET"
          },
          {
            headers: {
              "data-baseurl-namespace": apiConstants.URLS.AUTHX
            }
          }
        )
        .then(response => {
          const resData = response.data;
          if (resData.status === "error") {
            this.handleErrors(resData);
          } else {
            this.stats.total_signups = resData.total;
          }
        })
        .catch(() => {
          this.notification("Error", "An unknown error occurred", "danger");
        });
    },
    getAppLogins() {
      const { start_at, end_at } = this.period;
      const filter_by = "done_at";
      const queries = `start_at=${start_at}&end_at=${end_at}&filter_by=${filter_by}`;

      const apiUrl = `apps/${this.app.code}/stats/agg/logins?${queries}`;
      this.$http
        .post(
          apiUrl,
          {
            method: "GET"
          },
          {
            headers: {
              "data-baseurl-namespace": apiConstants.URLS.AUTHX
            }
          }
        )
        .then(response => {
          const resData = response.data;
          if (resData.status === "error") {
            this.handleErrors(resData);
          } else {
            this.stats.total_logins = resData.total;
          }
        })
        .catch(() => {
          this.notification("Error", "An unknown error occurred", "danger");
        });
    },
    getAppActiveUsers() {
      const { start_at, end_at } = this.period;
      const filter_by = "activities.done_at";
      const queries = `start_at=${start_at}&end_at=${end_at}&filter_by=${filter_by}`;

      const apiUrl = `apps/${this.app.code}/stats/agg/users?${queries}`;
      this.$http
        .post(
          apiUrl,
          {
            method: "GET"
          },
          {
            headers: {
              "data-baseurl-namespace": apiConstants.URLS.AUTHX
            }
          }
        )
        .then(response => {
          const resData = response.data;
          if (resData.status === "error") {
            this.handleErrors(resData);
            this.fetching = false;
          } else {
            this.stats.active_users = resData.total;
            this.fetching = false;
          }
        })
        .catch(() => {
          this.notification("Error", "An unknown error occurred", "danger");
          this.fetching = false;
        });
    },
    setAuthxApp(data) {
      this.$store.commit(mutations.SET_AUTHX_APP, data);
    }
  }
};
</script>

<style lang="scss" scoped>
.app-summary {
  background: #fff;
  padding: 30px 20px;
  border-radius: 8px;

  &__title {
    display: flex;
    align-items: flex-start;
    padding-bottom: 20px;
    margin-bottom: 20px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);

    .image {
      margin-right: 15px;
    }

    .title {
      h6 {
        font-size: 1rem;
        margin-bottom: 2px;
      }

      p {
        font-size: 0.75rem;
        line-height: 1.6;
      }
    }
  }

  &__switch {
    margin-bottom: 20px;
    display: flex;
    justify-content: center;
  }

  &__body {
    min-height: 200px;
    margin-bottom: 20px;
    padding: 5px;

    .property {
      &:not(:last-child) {
        margin-bottom: 15px;
      }

      > span {
        font-size: 0.75rem;
        opacity: 0.5;
        margin-bottom: 3px;
      }

      p {
        display: flex;
        align-items: center;
        justify-content: space-between;

        .long {
          display: inline-block;
          width: 85%;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        i {
          cursor: pointer;
          color: var(--ra-primary);
          font-weight: 500;
        }
      }

      &.app-code {
        padding: 10px 15px;
        border-radius: 8px;
        background: #fbe0e6;

        p {
          font-weight: 500;
        }
      }
    }
  }

  &__footer {
    display: flex;
    justify-content: center;
    align-items: center;
  }
}

.app-overview {
  &__action {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-bottom: 20px;

    p {
      margin-right: 12px;
    }
  }
}
</style>
